import { createTheme, useTheme, type PaletteMode } from '@mui/material/styles'

/**
 * Design tokens shared by every screen. Components read these instead of hard-coding colors.
 * Primary is the emerald of the Orbimail logo (#005040); gold is the "planet" accent, used only for unread markers.
 */
export const tokens = (mode: PaletteMode) =>
  mode === 'light'
    ? { canvas: '#f1f5f3', surface: '#ffffff', selected: '#d2e9e0', hover: '#e4ebe7', readRow: '#edf2ef', unreadRow: '#ffffff', border: '#dbe4df', rail: '#f1f5f3', accent: '#005040', gold: '#e8a93b', composeBg: '#005040', composeFg: '#ffffff' }
    : { canvas: '#0c1512', surface: '#141e1a', selected: '#0e4636', hover: '#22302a', readRow: '#101915', unreadRow: '#182420', border: '#27352f', rail: '#0c1512', accent: '#4fc3a1', gold: '#f0b94d', composeBg: '#4fc3a1', composeFg: '#03251b' }

export const useTokens = () => tokens(useTheme().palette.mode)

/** The one theme. Light/dark palettes, compact-density option, custom typography. */
export function buildTheme(mode: PaletteMode, density: 'compact' | 'comfortable') {
  const t = tokens(mode)
  const compact = density === 'compact'
  return createTheme({
    palette: {
      mode,
      primary: { main: t.accent },
      info: { main: t.accent, contrastText: mode === 'light' ? '#ffffff' : '#03251b' },
      secondary: { main: mode === 'light' ? '#b45309' : '#f0b94d' },
      background: { default: t.canvas, paper: t.surface },
      divider: t.border,
      // Both pass 4.5:1 on canvas and surface (verified by axe in e2e).
      text: mode === 'light' ? { primary: '#16201c', secondary: '#42504a' } : { primary: '#e6ede9', secondary: '#b9c6be' },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '"Manrope Variable", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: compact ? 13 : 14,
      button: { textTransform: 'none', fontWeight: 600 },
    },
    spacing: compact ? 7 : 8,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '::selection': { background: t.selected, color: mode === 'light' ? '#00281f' : '#e6fbf3' },
          ':focus-visible': { outlineColor: t.accent },
          'time, .tabular': { fontVariantNumeric: 'tabular-nums' },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': { background: mode === 'light' ? '#c3cec8' : '#3a4a43', borderRadius: 8, border: '2px solid transparent', backgroundClip: 'content-box' },
        },
      },
      MuiIconButton: { defaultProps: { size: compact ? 'small' : 'medium' } },
      MuiChip: { defaultProps: { size: 'small' } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiTooltip: { defaultProps: { arrow: true, enterDelay: 400 } },
      MuiMenu: { styleOverrides: { paper: { borderRadius: 12, minWidth: 200 } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
    },
  })
}
